import { Controller } from "@hotwired/stimulus"

// The reader's margin (xl screens): every highlight the reader made, and every public question,
// pinned in the right rail at the height of its passage — Tufte-style sidenotes.
//
// It never touches the story text. It reads the <mark>s that text_selection_controller paints
// into .story-body and watches for new ones, so saving or removing a highlight updates the margin
// with no extra wiring. Notes that would overlap are pushed down; the gap is constant.
const GAP = 14
const LABELS = { insight: "Insight", quote: "Quote", statistic: "Statistic", question: "Question", idea: "Idea" }

export default class extends Controller {
  static targets = ["rail", "hint", "body"]
  static values = { signedIn: Boolean }

  connect() {
    if (!this.hasRailTarget || !this.hasBodyTarget) return
    this.notes = new Map()
    this.ideas = this.readIdeas()

    this.schedule = () => {
      if (this.frame) return
      this.frame = requestAnimationFrame(() => {
        this.frame = null
        this.layout()
      })
    }

    this.mutations = new MutationObserver(this.schedule)
    this.mutations.observe(this.bodyTarget, { childList: true, subtree: true })
    // Fonts swapping in and lazy images change paragraph heights after first paint.
    this.resizes = new ResizeObserver(this.schedule)
    this.resizes.observe(this.bodyTarget)
    window.addEventListener("resize", this.schedule, { passive: true })

    this.onMarkOver = (event) => this.linkFromMark(event, true)
    this.onMarkOut = (event) => this.linkFromMark(event, false)
    this.bodyTarget.addEventListener("mouseover", this.onMarkOver)
    this.bodyTarget.addEventListener("mouseout", this.onMarkOut)

    this.schedule()
  }

  disconnect() {
    this.mutations?.disconnect()
    this.resizes?.disconnect()
    window.removeEventListener("resize", this.schedule)
    this.bodyTarget?.removeEventListener("mouseover", this.onMarkOver)
    this.bodyTarget?.removeEventListener("mouseout", this.onMarkOut)
    if (this.frame) cancelAnimationFrame(this.frame)
  }

  readIdeas() {
    const ideas = {}
    try {
      const list = JSON.parse(document.getElementById("story-user-highlights")?.textContent || "[]")
      for (const h of list) if (h.idea_text?.trim()) ideas[String(h.id)] = h.idea_text.trim()
    } catch (_) {}
    return ideas
  }

  groups() {
    const groups = new Map()
    for (const mark of this.bodyTarget.querySelectorAll("mark.user-highlight")) {
      const key = mark.dataset.highlightId ? `h${mark.dataset.highlightId}` : mark.dataset.paragraphAnchor ? `q${mark.dataset.paragraphAnchor}` : null
      if (!key) continue
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(mark)
    }
    return groups
  }

  layout() {
    if (getComputedStyle(this.railTarget).display === "none") return

    const groups = this.groups()
    for (const [key, el] of this.notes) {
      if (!groups.has(key)) {
        el.remove()
        this.notes.delete(key)
      }
    }

    const railTop = this.railTarget.getBoundingClientRect().top
    const placed = []
    for (const [key, marks] of groups) {
      let note = this.notes.get(key)
      if (!note) {
        note = this.buildNote(key, marks)
        this.railTarget.appendChild(note)
        this.notes.set(key, note)
      }
      note.marks = marks
      placed.push({ note, top: marks[0].getBoundingClientRect().top - railTop })
    }

    placed.sort((a, b) => a.top - b.top)
    let floor = 0
    placed.forEach(({ note, top }, i) => {
      const y = Math.max(top, floor)
      note.style.top = `${Math.round(y)}px`
      floor = y + note.offsetHeight + GAP
      if (!note.classList.contains("is-placed")) {
        note.style.transitionDelay = `${Math.min(i, 6) * 40}ms`
        requestAnimationFrame(() => {
          note.classList.add("is-placed")
          setTimeout(() => (note.style.transitionDelay = ""), 600)
        })
      }
    })

    if (this.hasHintTarget) this.hintTarget.hidden = placed.length > 0
  }

  buildNote(key, marks) {
    const first = marks[0]
    const category = (first.className.match(/user-highlight--(\w+)/) || [])[1] || "quote"
    const isPublicQuestion = key.startsWith("q") || (category === "question" && first.dataset.paragraphAnchor)
    const quote = marks.map((m) => m.textContent).join(" ").replace(/\s+/g, " ").trim()

    const note = document.createElement("button")
    note.type = "button"
    note.className = `margin-note margin-note--${category}`

    const label = document.createElement("span")
    label.className = "margin-note__label"
    label.textContent = isPublicQuestion ? "Readers asked" : LABELS[category] || "Highlight"
    note.appendChild(label)

    const q = document.createElement("span")
    q.className = "margin-note__quote"
    q.textContent = quote
    note.appendChild(q)

    const idea = first.dataset.highlightId && this.ideas[first.dataset.highlightId]
    if (idea) {
      const body = document.createElement("span")
      body.className = "margin-note__body"
      body.textContent = idea
      note.appendChild(body)
    }
    if (isPublicQuestion) {
      const cta = document.createElement("span")
      cta.className = "margin-note__cta"
      cta.textContent = "Open the thread →"
      note.appendChild(cta)
    }
    for (const span of note.querySelectorAll("span")) span.style.display = "block"

    note.setAttribute("aria-label", `${label.textContent}: ${quote.slice(0, 120)}`)
    note.addEventListener("mouseenter", () => this.setLinked(note, true))
    note.addEventListener("mouseleave", () => this.setLinked(note, false))
    note.addEventListener("focus", () => this.setLinked(note, true))
    note.addEventListener("blur", () => this.setLinked(note, false))
    note.addEventListener("click", () => this.activate(note, isPublicQuestion))
    return note
  }

  activate(note, isQuestion) {
    const mark = note.marks?.[0]
    if (!mark) return
    // Questions open the existing thread panel; text_selection_controller listens for this click.
    if (isQuestion) {
      mark.click()
      return
    }
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    mark.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" })
    this.setLinked(note, true)
    clearTimeout(note.unlinkTimer)
    note.unlinkTimer = setTimeout(() => this.setLinked(note, false), 1400)
  }

  setLinked(note, on) {
    note.classList.toggle("is-linked", on)
    note.marks?.forEach((m) => m.classList.toggle("is-linked", on))
  }

  linkFromMark(event, on) {
    const mark = event.target.closest?.("mark.user-highlight")
    if (!mark || !this.notes) return
    const key = mark.dataset.highlightId ? `h${mark.dataset.highlightId}` : `q${mark.dataset.paragraphAnchor}`
    const note = this.notes.get(key)
    if (note) this.setLinked(note, on)
  }
}
